// src/cockpit4/workspaces/HermesWorkspace.tsx
// ============================================
// Hermes Workspace - 全屏 Hermes 工作区（v6 专业级布局重构）
//
// 布局：
//   - 顶部：分组标签栏（对话 | P0 组 | P1 组 | P2 组，功能按需固定）
//   - 左 58%：HermesChat 全屏对话（主战场）
//   - 右 42%：全局状态条 + 单容器 4 Tab（执行中 / 任务 / 洞察 / 产物）
//
// 设计原则（私募级工作台范式）：
//   1. 单主焦点：对话是唯一主战场，右侧为上下文感知辅助栏
//   2. 单容器单滚动：右侧一个容器，Tab 内各自滚动，无嵌套壳
//   3. 上下文联动：意图路由出现 → 自动切"执行中"，产物生成 → 自动切"产物"
//   4. 信息去重：内容包仅存在于"产物"Tab
// ============================================

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Brain, Bot, Newspaper, FileText, X, ExternalLink, Loader2, Download, Tag, ChevronDown, ChevronRight, Clapperboard, MessageSquare, Database, Cpu, Bell, CalendarClock, FileBarChart, Network, Mic, Scale, GraduationCap, ShieldCheck, ListOrdered } from "lucide-react";
import HermesChat from "../../components/Hermes/HermesChat";
import HermesTaskPanel from "../../components/Hermes/Tasks/HermesTaskPanel";
import { HermesThinking } from "../../components/Cockpit";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { useHermesMessages } from "../../hooks/useHermes";
import { useCockpit4Store } from "../store";
// 轻量级 Markdown 渲染器（用户需求 #8：替代 <pre> 原样显示，避免符号乱显示）
import SimpleMarkdownRenderer from "../components/SimpleMarkdownRenderer";
// P0/P1 新增组件导入
import HermesMemoryPanel from "../components/HermesMemoryPanel";
import HermesTaskExecutor from "../components/HermesTaskExecutor";
import HermesNotificationCenter from "../components/HermesNotificationCenter";
import HermesScheduleManager from "../components/HermesScheduleManager";
import HermesReportGenerator from "../components/HermesReportGenerator";
import HermesCrossDeptCoordinator from "../components/HermesCrossDeptCoordinator";
import HermesMultimodalInput from "../components/HermesMultimodalInput";
// P2-功能8：决策支持
import HermesDecisionSupport from "../components/HermesDecisionSupport";
// P2-功能9：学习与成长
import HermesLearningGrowth from "../components/HermesLearningGrowth";
// P2-功能10：代理人模式
import HermesAgentMode from "../components/HermesAgentMode";

// ============================================
// 每日动态条目类型
// ============================================

/** 新闻分类 */
type NewsCategory = "AI" | "科技" | "商业" | "行业" | "产品" | "其他";

interface NewsDigestItem {
  /** 日期（YYYY-MM-DD） */
  date: string;
  /** 文件名（用于 get_digest 查询） */
  filename?: string;
  /** 标题（取自 TopStory 或日期） */
  title: string;
  /** 摘要 */
  summary: string;
  /** 来源标签（OpenAI/Anthropic 等） */
  sourceLabel?: string;
  /** 原文链接 */
  url?: string;
  /** 分类（自动推断） */
  category: NewsCategory;
}

/** 内容包条目 */
interface ContentPackageItem {
  filename: string;
  createdAt: string;
  title: string;
}

/** 内容预览 Modal 状态 */
interface PreviewState {
  title: string;
  content: string;
  loading: boolean;
  icon: "news" | "content";
  filename?: string;
  date?: string;
}

// ============================================
// 新闻分类配置（颜色 + 关键词）
// ============================================

const CATEGORY_CONFIG: Record<NewsCategory, { color: string; bg: string; border: string; keywords: string[] }> = {
  AI: {
    color: "#A78BFA",
    bg: "rgba(167, 139, 250, 0.08)",
    border: "rgba(167, 139, 250, 0.25)",
    keywords: ["AI", "GPT", "Claude", "Anthropic", "OpenAI", "DeepSeek", "GLM", "Qwen", "LLM", "Gemini", "ChatGPT", "通义", "智谱", "大模型", "模型", "Anthropic", "Reasoning"],
  },
  科技: {
    color: "#60A5FA",
    bg: "rgba(96, 165, 250, 0.08)",
    border: "rgba(96, 165, 250, 0.25)",
    keywords: ["手机", "芯片", "GPU", "Apple", "iPhone", "Tesla", "电池", "半导体", "处理器", "显卡", "英伟达", "Nvidia", "AMD", "Intel", "硬件", "设备"],
  },
  商业: {
    color: "#34D399",
    bg: "rgba(52, 211, 153, 0.08)",
    border: "rgba(52, 211, 153, 0.25)",
    keywords: ["融", "亿", "美元", "估值", "IPO", "收购", "投资", "营收", "财报", "融资", "市值", "股票", "上市", "并购", "ARR"],
  },
  行业: {
    color: "#FBBF24",
    bg: "rgba(251, 191, 36, 0.08)",
    border: "rgba(251, 191, 36, 0.25)",
    keywords: ["市场", "行业", "趋势", "报告", "调研", "预测", "增长", "渗透", "份额", "展望", "白皮书"],
  },
  产品: {
    color: "#F472B6",
    bg: "rgba(244, 114, 182, 0.08)",
    border: "rgba(244, 114, 182, 0.25)",
    keywords: ["发布", "上线", "推出", "更新", "Release", "Launch", "Beta", "Preview", "功能", "版本", "SDK", "API"],
  },
  其他: {
    color: "#9CA3AF",
    bg: "rgba(156, 163, 175, 0.06)",
    border: "rgba(156, 163, 175, 0.2)",
    keywords: [],
  },
};

/** 根据标题关键词自动推断新闻分类 */
function classifyNews(title: string, summary: string = ""): NewsCategory {
  const text = `${title} ${summary}`;
  const categories: NewsCategory[] = ["AI", "科技", "商业", "行业", "产品"];
  for (const cat of categories) {
    const cfg = CATEGORY_CONFIG[cat];
    if (cfg.keywords.some((kw) => text.includes(kw))) {
      return cat;
    }
  }
  return "其他";
}

// ============================================
// 内容预览 Modal（新闻 / 内容包 共用）
// ============================================

function ContentPreviewModal({ preview, onClose }: { preview: PreviewState | null; onClose: () => void }) {
  return (
    <AnimatePresence>
      {preview && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-8"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, y: 10 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 10 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-4xl max-h-[85vh] flex flex-col rounded-xl border border-white/10 bg-[#0a0e1a] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
              <div className="flex items-center gap-2">
                {preview.icon === "news" ? (
                  <Newspaper size={14} className="text-amber-400" />
                ) : (
                  <FileText size={14} className="text-emerald-400" />
                )}
                <h3 className="text-[14px] font-semibold text-white">{preview.title}</h3>
                {preview.filename && (
                  <span className="text-[11px] font-mono text-gray-600 ml-2 px-1.5 py-0.5 rounded bg-white/[0.04]">
                    {preview.filename}
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Body - Markdown 内容 */}
            <div className="flex-1 min-h-0 overflow-y-auto p-5">
              {preview.loading ? (
                <div className="flex items-center justify-center py-12 text-gray-500">
                  <Loader2 size={16} className="animate-spin mr-2" />
                  <span className="text-[13px]">正在从 Obsidian Vault 加载...</span>
                </div>
              ) : (
                <SimpleMarkdownRenderer content={preview.content} className="text-[12px]" />
              )}
            </div>

            {/* Modal Footer - 数据来源标识 */}
            <div className="flex items-center justify-between px-5 py-2 border-t border-white/[0.06] bg-white/[0.01]">
              <span className="text-[11px] font-mono text-gray-600">
                数据来源：Obsidian Vault · XEAOS/Daily/
              </span>
              <span className="text-[11px] font-mono text-gray-600">
                Hermes 自动生成
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ============================================
// 每日动态面板（单栏新闻 · 可点击查看完整内容）
// ============================================
// 已导出：随「抽屉接管」分散至 营销中心·动态洞察
export function DailyDynamicsPanel() {
  const [newsItems, setNewsItems] = useState<NewsDigestItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [crawling, setCrawling] = useState(false);
  const [crawlMessage, setCrawlMessage] = useState<string | null>(null);
  const [error, setError] = useState(false);
  // 每个分类的折叠状态：默认折叠（仅显示前 3 条），点击展开后显示全部（最多 8 条）
  const [expandedCategories, setExpandedCategories] = useState<Set<NewsCategory>>(new Set());
  // 内容预览 Modal 状态
  const [preview, setPreview] = useState<PreviewState | null>(null);

  // 加载新闻摘要
  const loadData = useCallback(async (signal?: { cancelled: boolean }) => {
    setLoading(true);
    setError(false);
    try {
      // 等 RuntimeManager 初始化完成（最多 6 秒）
      for (let i = 0; i < 20; i++) {
        if (signal?.cancelled) return;
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => window.setTimeout(r, 300));
      }
      if (signal?.cancelled) return;
      if (!RuntimeManager.isInitialized()) {
        setError(true);
        return;
      }

      // 拉取最近 7 天新闻摘要
      const newsResp = await RuntimeManager.query(RuntimeName.News, {
        type: "list_digests",
        limit: 7,
      });

      if (signal?.cancelled) return;

      // 处理新闻摘要：展开所有 topStories，按分类聚合
      const newsRaw: any = newsResp?.success ? newsResp.data : null;
      const newsData: any[] = Array.isArray(newsRaw)
        ? newsRaw
        : (newsRaw && Array.isArray(newsRaw.data) ? newsRaw.data : []);
      const news: NewsDigestItem[] = [];
      for (const d of newsData) {
        const date = String(d?.date || "");
        const filename = String(d?.filename || "");
        const topStories: any[] = Array.isArray(d?.topStories) ? d.topStories : [];
        if (topStories.length === 0) {
          // 白皮书铁律一：无数据时不显示占位假条目，跳过该日期
          continue;
        } else {
          for (const story of topStories) {
            const title = String(story?.title || story?.headline || "");
            const summary = String(story?.headline || "");
            news.push({
              date,
              filename,
              title,
              summary,
              sourceLabel: String(story?.sourceLabel || ""),
              url: story?.url ? String(story.url) : undefined,
              // 优先使用后端返回的 category，否则按标题自动分类
              category: (story?.category as NewsCategory) || classifyNews(title, summary),
            });
          }
        }
      }
      setNewsItems(news);
    } catch (err) {
      if (!signal?.cancelled) {
        setError(true);
        setNewsItems([]);
      }
    } finally {
      if (!signal?.cancelled) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const signal = { cancelled: false };
    loadData(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [loadData]);

  // 触发新闻采集（调用 NewsRuntime.fetch_today）
  const handleCrawlNews = async () => {
    if (crawling) return;
    setCrawling(true);
    setCrawlMessage("正在从 22 个新闻源采集（AI 厂商 + 中文媒体/财经）...约需 30 秒，请稍候");
    try {
      if (!RuntimeManager.isInitialized()) {
        setCrawlMessage("RuntimeManager 尚未初始化");
        return;
      }
      // v6.30 采集超时保护：90s 上限（22 源含海外源，部分超时属正常），避免无限等待
      const resp = await Promise.race([
        RuntimeManager.execute(RuntimeName.News, "fetch_today", {}),
        new Promise<null>((resolve) =>
          window.setTimeout(() => resolve(null), 90 * 1000)
        ),
      ]);
      if (resp && resp.success) {
        setCrawlMessage("采集完成，正在刷新列表...");
        // 采集完成后刷新新闻列表
        await loadData();
        setCrawlMessage(null);
      } else if (resp === null) {
        setCrawlMessage("采集超时（90s）。海外源可能被墙/JS渲染，已改用中文源继续；可稍后重试或看营销中心热点追踪");
      } else {
        setCrawlMessage(`采集完成（部分源失败属正常）：${(resp?.data as any)?.articles?.length ?? 0} 条`);
        await loadData();
        setCrawlMessage(null);
      }
    } catch (err: any) {
      setCrawlMessage(`采集异常：${err?.message || err}`);
    } finally {
      setCrawling(false);
    }
  };

  // 点击新闻条目 → 加载完整新闻内容
  const openNews = async (item: NewsDigestItem) => {
    setPreview({ title: `新闻摘要 ${item.date}`, content: "", loading: true, icon: "news", date: item.date, filename: item.filename });
    try {
      const resp = await RuntimeManager.query(RuntimeName.News, {
        type: "get_digest",
        date: item.date,
      });
      const raw: any = resp?.success ? resp.data : null;
      const content: string =
        typeof raw === "string"
          ? raw
          : (raw && typeof raw.content === "string"
            ? raw.content
            : (raw && typeof raw.markdown === "string"
              ? raw.markdown
              : JSON.stringify(raw, null, 2)));
      setPreview({ title: `新闻摘要 ${item.date}`, content, loading: false, icon: "news", date: item.date, filename: item.filename });
    } catch (err: any) {
      setPreview({ title: `新闻摘要 ${item.date}`, content: `加载失败：${err?.message || err}`, loading: false, icon: "news", date: item.date, filename: item.filename });
    }
  };

  // 将新闻按分类分组（保留顺序：AI → 科技 → 商业 → 行业 → 产品 → 其他）
  const groupedNews: { category: NewsCategory; items: NewsDigestItem[] }[] = [];
  const categoryOrder: NewsCategory[] = ["AI", "科技", "商业", "行业", "产品", "其他"];
  for (const cat of categoryOrder) {
    const items = newsItems.filter((it) => it.category === cat);
    if (items.length > 0) {
      groupedNews.push({ category: cat, items });
    }
  }

  return (
    <>
      <div className="h-full flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <Newspaper size={14} className="text-amber-400" />
            <span className="text-[13px] font-medium text-white">每日动态 · 新闻洞察</span>
            <span className="text-[10px] font-mono text-gray-600">来自 Obsidian Vault</span>
          </div>
          <div className="flex items-center gap-2">
            {crawlMessage && (
              <span className="text-[10px] font-mono text-amber-400/80">{crawlMessage}</span>
            )}
            <button
              onClick={handleCrawlNews}
              disabled={crawling}
              title="立即采集 22 个新闻源（AI 厂商 + 中文科技 + 财经）并生成今日摘要"
              className="flex items-center gap-1 px-2 py-1 rounded-md border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {crawling ? (
                <Loader2 size={11} className="animate-spin text-amber-400" />
              ) : (
                <Download size={11} className="text-amber-400" />
              )}
              <span className="text-[11px] font-mono text-amber-300">
                {crawling ? "采集中..." : "采集新闻"}
              </span>
            </button>
          </div>
        </div>

        {/* 内容区 - 单栏新闻（按分类分段） */}
        <div className="flex-1 min-h-0 flex flex-col p-2 overflow-hidden">
          <div className="text-[11px] font-mono text-gray-500 uppercase tracking-wider mb-1 px-1 flex items-center justify-between">
            <span>新闻摘要（{newsItems.length} 条 · 7 天）</span>
            {groupedNews.length > 0 && (
              <span className="text-[10px] text-gray-600 normal-case">
                {groupedNews.length} 个分类
              </span>
            )}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-2">
            {loading ? (
              <div className="text-[12px] text-gray-500 italic">加载中...</div>
            ) : newsItems.length === 0 ? (
              <div className="text-[12px] text-gray-600 italic">暂无数据</div>
            ) : (
              groupedNews.map(({ category, items }) => {
                const cfg = CATEGORY_CONFIG[category];
                // 折叠状态：默认只显示前 3 条，展开后显示最多 8 条
                const isExpanded = expandedCategories.has(category);
                const COLLAPSED_COUNT = 3;
                const EXPANDED_MAX = 8;
                const visibleItems = isExpanded
                  ? items.slice(0, EXPANDED_MAX)
                  : items.slice(0, COLLAPSED_COUNT);
                const hasMore = items.length > COLLAPSED_COUNT;
                return (
                  <div key={category} className="space-y-1">
                    {/* 分类标题（可点击折叠/展开） */}
                    <button
                      onClick={() => {
                        setExpandedCategories((prev) => {
                          const next = new Set(prev);
                          if (next.has(category)) {
                            next.delete(category);
                          } else {
                            next.add(category);
                          }
                          return next;
                        });
                      }}
                      className="w-full flex items-center gap-1.5 px-1 py-0.5 hover:bg-white/[0.03] rounded transition-colors"
                      title={isExpanded ? "点击折叠" : "点击展开全部"}
                    >
                      {isExpanded ? (
                        <ChevronDown size={10} style={{ color: cfg.color }} />
                      ) : (
                        <ChevronRight size={10} style={{ color: cfg.color }} />
                      )}
                      <Tag size={10} style={{ color: cfg.color }} />
                      <span
                        className="text-[11px] font-mono font-semibold px-1.5 py-0.5 rounded"
                        style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}
                      >
                        {category}
                      </span>
                      <span className="text-[10px] font-mono text-gray-600">
                        {items.length}
                      </span>
                      {hasMore && !isExpanded && (
                        <span className="ml-auto text-[9px] font-mono text-gray-600">
                          +{items.length - COLLAPSED_COUNT} 条
                        </span>
                      )}
                      {isExpanded && items.length > EXPANDED_MAX && (
                        <span className="ml-auto text-[9px] font-mono text-gray-600">
                          仅显示前 {EXPANDED_MAX} 条
                        </span>
                      )}
                    </button>
                    {/* 分类下的新闻条目（按折叠状态限制条数） */}
                    {visibleItems.map((item, i) => (
                      <button
                        key={`news-${category}-${i}`}
                        onClick={() => openNews(item)}
                        className="w-full text-left rounded border border-white/[0.04] bg-white/[0.01] px-2 py-1.5 hover:border-amber-500/30 hover:bg-amber-500/[0.03] transition-colors group cursor-pointer"
                      >
                        <div className="flex items-center justify-between mb-0.5 gap-1">
                          <span className="text-[11px] font-mono text-amber-400/90">
                            {item.date}
                          </span>
                          <div className="flex items-center gap-1 min-w-0">
                            {item.sourceLabel && (
                              <span className="text-[10px] font-mono text-gray-500 truncate">
                                {item.sourceLabel}
                              </span>
                            )}
                            <ExternalLink size={10} className="text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                          </div>
                        </div>
                        <div className="text-[13px] text-gray-200 leading-snug line-clamp-2">
                          {item.title}
                        </div>
                        {item.summary && item.summary !== item.title && (
                          <div className="text-[11px] text-gray-500 leading-snug line-clamp-1 mt-0.5">
                            {item.summary}
                          </div>
                        )}
                      </button>
                    ))}
                    {/* 折叠状态下的"展开更多"提示 */}
                    {hasMore && !isExpanded && (
                      <button
                        onClick={() => {
                          setExpandedCategories((prev) => new Set(prev).add(category));
                        }}
                        className="w-full text-center py-0.5 text-[9px] font-mono text-gray-500 hover:text-amber-400 transition-colors"
                      >
                        展开剩余 {items.length - COLLAPSED_COUNT} 条 ↓
                      </button>
                    )}
                    {/* 展开状态下的"收起"提示 */}
                    {isExpanded && (
                      <button
                        onClick={() => {
                          setExpandedCategories((prev) => {
                            const next = new Set(prev);
                            next.delete(category);
                            return next;
                          });
                        }}
                        className="w-full text-center py-0.5 text-[9px] font-mono text-gray-500 hover:text-amber-400 transition-colors"
                      >
                        收起 ↑
                      </button>
                    )}
                  </div>
                );
              })
            )}
            {error && newsItems.length === 0 && !loading && (
              <div className="text-[12px] text-gray-600 italic">暂无数据</div>
            )}
          </div>
        </div>
      </div>

      {/* 内容预览 Modal */}
      <ContentPreviewModal preview={preview} onClose={() => setPreview(null)} />
    </>
  );
}

// ============================================
// 内容包列表（Obsidian 内容包 · 归入"产物"Tab）
// ============================================
// 已导出：随「抽屉接管」分散至 内容工厂·内容包
export function ContentPackagesList() {
  const [contentItems, setContentItems] = useState<ContentPackageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [preview, setPreview] = useState<PreviewState | null>(null);

  const loadData = useCallback(async () => {
    try {
      for (let i = 0; i < 20; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => window.setTimeout(r, 300));
      }
      if (!RuntimeManager.isInitialized()) {
        setError(true);
        return;
      }
      const contentResp = await RuntimeManager.query(RuntimeName.Content, {
        type: "list_packages",
        limit: 8,
      });
      const contentRaw: any = contentResp?.success ? contentResp.data : null;
      const contentData: any[] = Array.isArray(contentRaw)
        ? contentRaw
        : (contentRaw && Array.isArray(contentRaw.data) ? contentRaw.data : []);
      const content: ContentPackageItem[] = contentData.map((c: any) => ({
        filename: String(c?.filename || ""),
        createdAt: String(c?.createdAt || c?.updatedAt || ""),
        title: String(c?.filename || "").replace(/\.md$/i, ""),
      }));
      setContentItems(content);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 点击内容包条目 → 加载完整内容包
  const openContent = async (item: ContentPackageItem) => {
    setPreview({ title: item.title, content: "", loading: true, icon: "content", filename: item.filename });
    try {
      const resp = await RuntimeManager.query(RuntimeName.Content, {
        type: "get_package",
        filename: item.filename,
      });
      const raw: any = resp?.success ? resp.data : null;
      const content: string =
        typeof raw === "string"
          ? raw
          : (raw && typeof raw.content === "string"
            ? raw.content
            : (raw && typeof raw.markdown === "string"
              ? raw.markdown
              : JSON.stringify(raw, null, 2)));
      setPreview({ title: item.title, content, loading: false, icon: "content", filename: item.filename });
    } catch (err: any) {
      setPreview({ title: item.title, content: `加载失败：${err?.message || err}`, loading: false, icon: "content", filename: item.filename });
    }
  };

  return (
    <>
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <FileText size={13} className="text-emerald-400" />
            <span className="text-[12px] font-medium text-white">内容包</span>
            <span className="text-[10px] font-mono text-gray-600">{contentItems.length} 个</span>
          </div>
        </div>
        {/* 列表 */}
        <div className="p-2 space-y-1.5">
          {loading ? (
            <div className="text-[12px] text-gray-500 italic">加载中...</div>
          ) : contentItems.length === 0 ? (
            <div className="text-[12px] text-gray-600 italic">暂无数据</div>
          ) : (
            contentItems.map((item, i) => (
              <button
                key={`content-${i}`}
                onClick={() => openContent(item)}
                className="w-full text-left rounded border border-white/[0.04] bg-white/[0.01] px-2 py-1.5 hover:border-emerald-500/30 hover:bg-emerald-500/[0.03] transition-colors group cursor-pointer"
              >
                <div className="text-[13px] text-gray-200 leading-snug line-clamp-2">
                  {item.title}
                </div>
                {item.createdAt && (
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-[11px] font-mono text-gray-600">
                      {new Date(item.createdAt).toLocaleDateString("zh-CN")}
                    </span>
                    <ExternalLink size={10} className="text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                )}
              </button>
            ))
          )}
          {error && contentItems.length === 0 && !loading && (
            <div className="text-[12px] text-gray-600 italic">暂无数据</div>
          )}
        </div>
      </div>

      {/* 内容预览 Modal */}
      <ContentPreviewModal preview={preview} onClose={() => setPreview(null)} />
    </>
  );
}

// ============================================
// 最近生成产物预览（媒体工作室最近 5 个包）
// 点击"查看详情"跳转到媒体工作室
// ============================================

/** 根据包内脚本字段推断平台标识与展示色 */
function inferMediaPlatform(pkg: any): { name: string; color: string } {
  if (pkg?.articleScript) return { name: "公众号", color: "#10B981" };
  if (pkg?.douyinScript) return { name: "抖音", color: "#EC4899" };
  if (pkg?.xiaohongshuScript) return { name: "小红书", color: "#F43F5E" };
  if (pkg?.bilibiliScript) return { name: "B站", color: "#FB7299" };
  if (pkg?.videoScript) return { name: "视频", color: "#06B6D4" };
  return { name: "内容包", color: "#A855F7" };
}

function MediaPackagesPreview({ refreshTrigger }: { refreshTrigger: number }) {
  // 媒体工作室最近生成的包列表
  const [mediaPackages, setMediaPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // 跳转到媒体工作室
  const setWorkspace = useCockpit4Store((s) => s.setWorkspace);

  // 加载最近 5 个媒体包
  const loadMediaPackages = useCallback(async () => {
    try {
      // 轮询等待 RuntimeManager 就绪（最多 6 秒）
      for (let i = 0; i < 20; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => window.setTimeout(r, 300));
      }
      if (!RuntimeManager.isInitialized()) {
        setLoading(false);
        return;
      }
      const resp = await RuntimeManager.query(RuntimeName.MediaStudio, {
        type: "list_packages",
      });
      if (resp?.success && resp.data) {
        const raw = resp.data as any;
        // 兼容多种返回结构：直接数组 / { packages: [] } / { data: [] }
        const list: any[] = Array.isArray(raw)
          ? raw
          : raw?.packages || raw?.data || [];
        // 后端已按 createdAt 倒序，前端再保险排序并取前 5
        const sorted = [...list].sort((a, b) =>
          String(b.createdAt || "").localeCompare(String(a.createdAt || ""))
        );
        setMediaPackages(sorted.slice(0, 5));
      }
    } catch (err) {
      console.warn("[HermesWorkspace] 加载媒体包失败:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 组件挂载时 + refreshTrigger 变化（Hermes 发送消息后）时加载
  useEffect(() => {
    loadMediaPackages();
  }, [loadMediaPackages, refreshTrigger]);

  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-3">
      {/* 头部：标题 + 跳转媒体工作室 */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Clapperboard size={13} className="text-purple-400" />
          <span className="text-[12px] font-medium text-white">最近生成产物</span>
          <span className="text-[10px] font-mono text-gray-600">
            来自媒体工作室
          </span>
        </div>
        <button
          onClick={() => setWorkspace("media_studio")}
          className="flex items-center gap-1 px-2 py-1 rounded-md border border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20 transition-colors"
          title="打开媒体工作室查看全部素材"
        >
          <ExternalLink size={10} className="text-purple-400" />
          <span className="text-[10px] font-mono text-purple-300">全部</span>
        </button>
      </div>

      {/* 内容区 */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {loading ? (
          <div className="flex items-center gap-2 text-[11px] text-gray-500 italic py-2">
            <Loader2 size={11} className="animate-spin" />
            加载中...
          </div>
        ) : mediaPackages.length === 0 ? (
          <div className="text-[11px] text-gray-600 italic py-2">
            暂无生成产物，可对我说"生成抖音脚本"触发生成
          </div>
        ) : (
          mediaPackages.map((pkg, i) => {
            const platform = inferMediaPlatform(pkg);
            // 缩略图：优先封面 url，其次九宫格首图，无图则用占位
            const thumb =
              pkg?.cover?.url ||
              pkg?.wechatCover?.url ||
              pkg?.douyinCover?.url ||
              (Array.isArray(pkg?.xiaohongshuGrid) && pkg.xiaohongshuGrid[0]?.url) ||
              "";
            const title = pkg?.topic || pkg?.title || `内容包 ${i + 1}`;
            const createdAt = pkg?.createdAt || "";
            return (
              <div
                key={pkg?.id || i}
                className="flex-shrink-0 w-[140px] rounded-lg border border-white/[0.06] bg-white/[0.01] overflow-hidden hover:border-purple-500/30 transition-colors group"
              >
                {/* 缩略图区域 */}
                <div className="h-[64px] bg-white/[0.03] flex items-center justify-center overflow-hidden">
                  {thumb ? (
                    <img
                      src={thumb}
                      alt={title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <FileText size={18} className="text-gray-600" />
                  )}
                </div>
                {/* 信息区域 */}
                <div className="p-1.5">
                  <div className="flex items-center gap-1 mb-0.5">
                    <span
                      className="text-[9px] font-mono px-1 py-0.5 rounded"
                      style={{
                        color: platform.color,
                        background: `${platform.color}1a`,
                      }}
                    >
                      {platform.name}
                    </span>
                    {createdAt && (
                      <span className="text-[9px] font-mono text-gray-600 ml-auto">
                        {new Date(createdAt).toLocaleDateString("zh-CN", {
                          month: "2-digit",
                          day: "2-digit",
                        })}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-200 leading-tight line-clamp-2 mb-1.5">
                    {title}
                  </div>
                  <button
                    onClick={() => setWorkspace("media_studio")}
                    className="w-full flex items-center justify-center gap-1 px-1.5 py-1 text-[9px] font-mono text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 rounded transition-colors"
                  >
                    <ExternalLink size={9} />
                    查看详情
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ============================================
// 功能分组 Tab 定义（P0 / P1 / P2）
// ============================================

const GROUP_TABS: Record<string, { id: string; label: string; icon: typeof Database }[]> = {
  P0: [
    { id: "memory", label: "企业记忆", icon: Database },
    { id: "executor", label: "任务执行", icon: Cpu },
    { id: "notification", label: "主动提醒", icon: Bell },
  ],
  P1: [
    { id: "schedule", label: "日程会议", icon: CalendarClock },
    { id: "report", label: "信息汇报", icon: FileBarChart },
    { id: "coordinate", label: "跨部门协调", icon: Network },
    { id: "multimodal", label: "多模态", icon: Mic },
  ],
  P2: [
    { id: "decision", label: "决策支持", icon: Scale },
    { id: "learning", label: "学习成长", icon: GraduationCap },
    { id: "agent_mode", label: "代理人", icon: ShieldCheck },
  ],
};

/** 扁平化所有功能 Tab（用于 pinned 显示） */
const ALL_FUNCTION_TABS: { id: string; label: string; icon: typeof Database }[] = Object.values(GROUP_TABS).flat();

// ============================================
// 右侧辅助栏 4 Tab 定义
// ============================================

type SideTabId = "executing" | "tasks" | "insights" | "artifacts";

const SIDE_TABS: { id: SideTabId; label: string; icon: typeof Brain }[] = [
  { id: "executing", label: "执行中", icon: Brain },
  { id: "tasks", label: "任务", icon: ListOrdered },
  { id: "insights", label: "洞察", icon: Newspaper },
  { id: "artifacts", label: "产物", icon: Clapperboard },
];

// ============================================
// HermesWorkspace 主组件
// ============================================

export default function HermesWorkspace() {
  // 读取共享的 Hermes 消息列表，提取最新路由信息传递给 HermesThinking
  const messages = useHermesMessages();
  // 顶部功能 Tab 状态：chat 为默认，其余为分组内选择的功能
  const [activeTab, setActiveTab] = useState<string>("chat");
  // 用户从分组下拉中固定的功能 Tab（可关闭）
  const [pinnedTabs, setPinnedTabs] = useState<string[]>([]);
  // 当前展开的分组下拉
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  // 右侧辅助栏当前 Tab
  const [sideTab, setSideTab] = useState<SideTabId>("executing");

  const routingInfo = useMemo(() => {
    // 从后往前找最近一条携带 intent/runtime/view 的 assistant 消息
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role === "assistant" && (msg.intent || msg.runtime || msg.view)) {
        return {
          intent: msg.intent ? String(msg.intent) : undefined,
          runtime: msg.runtime ? String(msg.runtime) : undefined,
          view: msg.view !== undefined ? String(msg.view) : undefined,
        };
      }
    }
    return { intent: undefined, runtime: undefined, view: undefined };
  }, [messages]);

  // 上下文联动：新的意图路由出现 → 自动切到"执行中"
  const prevRoutingKey = useRef("");
  useEffect(() => {
    const key = [routingInfo.intent, routingInfo.runtime, routingInfo.view].filter(Boolean).join("/");
    if (key && key !== prevRoutingKey.current) {
      prevRoutingKey.current = key;
      setSideTab("executing");
    }
  }, [routingInfo]);

  // 从分组下拉选择功能 → 激活并固定到标签栏
  const handlePickFunction = useCallback((tabId: string) => {
    setActiveTab(tabId);
    setPinnedTabs((prev) => (prev.includes(tabId) ? prev : [...prev, tabId]));
    setOpenGroup(null);
  }, []);

  // 关闭固定 Tab；若为当前激活则回到对话
  const handleUnpin = useCallback((tabId: string) => {
    setPinnedTabs((prev) => prev.filter((t) => t !== tabId));
    setActiveTab((prev) => (prev === tabId ? "chat" : prev));
  }, []);

  return (
    <div className="h-full w-full overflow-hidden p-4 flex flex-col">
      {/* ============ 顶部：分组标签栏 ============ */}
      <div className="flex-shrink-0 flex items-center gap-1 px-2 py-1.5 mb-2 rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl flex-wrap">
        {/* 对话（默认主战场） */}
        <button
          onClick={() => {
            setActiveTab("chat");
            setOpenGroup(null);
          }}
          className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-mono transition ${
            activeTab === "chat"
              ? "bg-purple-500/15 text-purple-300 border border-purple-500/30"
              : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.04] border border-transparent"
          }`}
        >
          <MessageSquare size={11} />
          对话
        </button>

        <div className="w-px h-4 bg-white/[0.08] mx-1" />

        {/* 分组胶囊：P0 / P1 / P2（点击展开下拉选择功能） */}
        {(["P0", "P1", "P2"] as const).map((group) => (
          <div key={group} className="relative">
            <button
              onClick={() => setOpenGroup(openGroup === group ? null : group)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-mono transition ${
                openGroup === group
                  ? "bg-white/[0.06] text-white border border-white/[0.12]"
                  : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.04] border border-transparent"
              }`}
            >
              <span className="uppercase">{group}</span>
              <ChevronDown size={10} className={openGroup === group ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>

            {/* 分组下拉菜单 */}
            <AnimatePresence>
              {openGroup === group && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="fixed inset-0 z-30"
                    onClick={() => setOpenGroup(null)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: -4, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-0 mt-1 z-40 min-w-[150px] rounded-lg border border-white/[0.1] bg-[#0d1220] p-1 shadow-2xl"
                  >
                    {GROUP_TABS[group].map((tab) => {
                      const Icon = tab.icon;
                      const pinned = pinnedTabs.includes(tab.id);
                      return (
                        <button
                          key={tab.id}
                          onClick={() => handlePickFunction(tab.id)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[11px] font-mono text-gray-300 hover:bg-purple-500/10 hover:text-purple-200 transition-colors"
                        >
                          <Icon size={11} className="text-purple-400" />
                          <span className="flex-1 text-left">{tab.label}</span>
                          {activeTab === tab.id && (
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                          )}
                          {pinned && activeTab !== tab.id && (
                            <span className="text-[9px] text-gray-600">已固定</span>
                          )}
                        </button>
                      );
                    })}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        ))}

        {/* 已固定的功能 Tab（可关闭） */}
        {pinnedTabs.length > 0 && <div className="w-px h-4 bg-white/[0.08] mx-1" />}
        {pinnedTabs.map((tabId) => {
          const tabInfo = ALL_FUNCTION_TABS.find((t) => t.id === tabId);
          if (!tabInfo) return null;
          const Icon = tabInfo.icon;
          const isActive = activeTab === tabId;
          return (
            <button
              key={tabId}
              onClick={() => setActiveTab(tabId)}
              className={`group flex items-center gap-1 px-2 py-1 rounded text-[11px] font-mono transition ${
                isActive
                  ? "bg-purple-500/15 text-purple-300 border border-purple-500/30"
                  : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.04] border border-transparent"
              }`}
              title={`${tabInfo.label}（点击切换）`}
            >
              <Icon size={11} />
              {tabInfo.label}
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnpin(tabId);
                }}
                className="ml-0.5 p-0.5 rounded hover:bg-white/[0.1] hover:text-red-400 transition-colors"
                title="关闭"
              >
                <X size={9} />
              </span>
            </button>
          );
        })}
      </div>

      {/* ============ 对话主战场 + 右侧辅助栏 ============ */}
      {activeTab === "chat" && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="flex-1 min-h-0 grid grid-cols-12 gap-3"
        >
          {/* 左 58%：HermesChat（主战场） */}
          <div className="col-span-7 h-full flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-md bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
                  <Sparkles size={14} className="text-purple-400" />
                </div>
                <div>
                  <h2 className="text-[15px] font-semibold text-white tracking-tight">
                    Hermes 助手
                  </h2>
                  <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                    AI 行政助手 · 全屏对话
                  </p>
                </div>
              </div>
              <span className="text-[10px] font-mono text-purple-400 uppercase tracking-wider">
                DeepSeek Powered
              </span>
            </div>

            {/* Chat 内容 */}
            <div className="flex-1 min-h-0">
              <HermesChat />
            </div>
          </div>

          {/* 右 42%：状态条 + 单容器 4 Tab 辅助工作台 */}
          <div className="col-span-5 h-full flex flex-col gap-3 min-h-0">
            {/* 全局状态条 */}
            <div className="flex-shrink-0 flex items-center justify-between px-3 py-1.5 rounded-lg border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[10px] font-mono text-emerald-300 uppercase tracking-wider">Online</span>
                </span>
                <span className="flex items-center gap-1 text-[10px] font-mono text-gray-400">
                  <Bot size={10} className="text-purple-400" />
                  DeepSeek · Hermes
                </span>
              </div>
              <div className="flex items-center gap-3 text-[10px] font-mono text-gray-500">
                <span className="flex items-center gap-1">
                  <MessageSquare size={10} />
                  消息 {messages.length}
                </span>
                <span className="flex items-center gap-1">
                  <Cpu size={10} />
                  路由 {routingInfo.runtime ? String(routingInfo.runtime) : "空闲"}
                </span>
                <span className="flex items-center gap-1">
                  <Brain size={10} />
                  意图 {routingInfo.intent ? String(routingInfo.intent) : "-"}
                </span>
              </div>
            </div>

            {/* 单容器 4 Tab 辅助工作台 */}
            <div className="flex-1 min-h-0 flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden">
              {/* Tab 头 */}
              <div className="flex-shrink-0 flex items-center gap-1 px-2 pt-2 pb-1.5 border-b border-white/[0.06]">
                {SIDE_TABS.map((t) => {
                  const Icon = t.icon;
                  const isActive = sideTab === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setSideTab(t.id)}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-mono transition-all ${
                        isActive
                          ? "bg-purple-500/15 text-purple-300 border border-purple-500/30"
                          : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.04] border border-transparent"
                      }`}
                    >
                      <Icon size={11} />
                      {t.label}
                    </button>
                  );
                })}
                <span className="ml-auto text-[9px] font-mono text-gray-600 uppercase tracking-wider">
                  Context Aware
                </span>
              </div>

              {/* Tab 内容（单容器单滚动） */}
              <div className="flex-1 min-h-0">
                {/* 执行中：HermesThinking 实时思考链 */}
                {sideTab === "executing" && (
                  <div className="h-full">
                    <HermesThinking
                      intent={routingInfo.intent}
                      runtime={routingInfo.runtime}
                      view={routingInfo.view}
                    />
                  </div>
                )}

                {/* 任务：HermesTaskPanel 完整版（独占高度） */}
                {sideTab === "tasks" && (
                  <div className="h-full">
                    <HermesTaskPanel />
                  </div>
                )}

                {/* 洞察：每日动态（新闻分类摘要） */}
                {sideTab === "insights" && (
                  <div className="h-full">
                    <DailyDynamicsPanel />
                  </div>
                )}

                {/* 产物：媒体产物 + 内容包（合并去重） */}
                {sideTab === "artifacts" && (
                  <div className="h-full overflow-y-auto p-3 space-y-3">
                    <MediaPackagesPreview refreshTrigger={messages.length} />
                    <ContentPackagesList />
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* ============ 功能 Tab 内容区 ============ */}
      {activeTab !== "chat" && (
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="flex-1 min-h-0 overflow-hidden"
        >
          {activeTab === "memory" && <HermesMemoryPanel />}
          {activeTab === "executor" && <HermesTaskExecutor />}
          {activeTab === "notification" && <HermesNotificationCenter />}
          {activeTab === "schedule" && <HermesScheduleManager />}
          {activeTab === "report" && <HermesReportGenerator />}
          {activeTab === "coordinate" && <HermesCrossDeptCoordinator />}
          {activeTab === "multimodal" && <HermesMultimodalInput />}

          {/* P2 组件 */}
          {activeTab === "decision" && <HermesDecisionSupport />}
          {activeTab === "learning" && <HermesLearningGrowth />}
          {activeTab === "agent_mode" && <HermesAgentMode />}
        </motion.div>
      )}
    </div>
  );
}
